import DatePicker from 'react-datepicker';
import { isClosedDate, isPickerTimeAvailable, temporaryClosureMessageKey } from '../booking-availability';

export function Step3Transfer({ formData, updateFormData, t }) {
    /* --------------------------------------------------
     * Helpers
     * -------------------------------------------------- */

    const pad = (n) => String(n).padStart(2, '0');

    const timeStringToDate = (time) => {
        if (!time) return null;
        const [hours, minutes] = time.split(':');
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        return date;
    };

    const dateToTimeString = (date) => {
        if (!date) return '';
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    /* --------------------------------------------------
     * Handlers
     * -------------------------------------------------- */

    const handleTransferChange = (e) => {
        const { name, value } = e.target;
        let updatedFields = { [name]: value };

        // Reset logic when transfer type changes
        if (name === 'transfer_type') {
            if (value === 'outbound') {
                updatedFields = {
                    ...updatedFields,
                    passengers_return: 0,
                    pickup_place: '',
                    pickup_place_detail: '',
                    disembark_detail: '',
                    disembark_time: '',
                    train_arrival_time: '',
                    pickup_time: '',
                };
            } else if (value === 'return') {
                updatedFields = {
                    ...updatedFields,
                    passengers_outbound: 0,
                    dropoff_place: '',
                    dropoff_place_detail: '',
                    embark_detail: '',
                    train_arrival_time: '',
                    pickup_time: '',
                };
            }
        }

        // Reset place details
        if (name === 'dropoff_place' && !['city', 'other'].includes(value)) {
            updatedFields.dropoff_place_detail = '';
        }
        if (name === 'pickup_place' && !['city', 'other'].includes(value)) {
            updatedFields.pickup_place_detail = '';
        }

        updateFormData(updatedFields);
    };

    const handleCheckboxChange = (e) => {
        const checked = e.target.checked;

        updateFormData({ has_transfer: checked });

        if (!checked) {
            updateFormData({
                transfer_type: '',
                passengers_outbound: 0,
                passengers_return: 0,
                dropoff_place: '',
                dropoff_place_detail: '',
                pickup_place: '',
                pickup_place_detail: '',
                embark_detail: '',
                disembark_detail: '',
                disembark_time: '',
                train_arrival_time: '',
                pickup_time: '',
            });
        }
    };

    // Time-only handlers (DatePicker)
    const handleTrainArrivalTimeChange = (date) => {
        updateFormData({
            train_arrival_time: dateToTimeString(date),
        });
    };

    const handlePickupTimeChange = (date) => {
        updateFormData({
            pickup_time: dateToTimeString(date),
        });
    };

    const handleDisembarkTimeChange = (date) => {
        updateFormData({
            disembark_time: dateToTimeString(date),
        });
    };

    /* --------------------------------------------------
     * Visibility logic
     * -------------------------------------------------- */

    const showOutboundFields =
        formData.has_transfer && ['outbound', 'outbound_return'].includes(formData.transfer_type);

    const showReturnFields = formData.has_transfer && ['return', 'outbound_return'].includes(formData.transfer_type);

    /* --------------------------------------------------
     * Render
     * -------------------------------------------------- */

    return (
        <div className="step-content">
            <h3>{t('stepper.step3.title')}</h3>
            {formData.has_transfer &&
                ((showOutboundFields && isClosedDate(formData.checkin_date)) ||
                    (showReturnFields && isClosedDate(formData.checkout_date))) && (
                    <p role="status">{t(temporaryClosureMessageKey)}</p>
                )}

            <div className="form-check form-switch form-switch-lg mb-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="hasTransferSwitch"
                    checked={formData.has_transfer}
                    onChange={handleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="hasTransferSwitch">
                    {t('stepper.transfer_option.label')}
                </label>
            </div>

            {formData.has_transfer && (
                <>
                    {/* Transfer type */}
                    <div className="row mt-1">
                        <div className="col-12 form-group">
                            <label className="form__label">{t('stepper.transfer_type.label')} *</label>
                            <select
                                className="select-form-group form-control"
                                name="transfer_type"
                                value={formData.transfer_type}
                                onChange={handleTransferChange}
                                required
                            >
                                <option value="" hidden>
                                    {t('stepper.transfer_type.placeholder')}
                                </option>
                                <option value="outbound">{t('stepper.transfer_type.option_one')}</option>
                                <option value="return">{t('stepper.transfer_type.option_two')}</option>
                                <option value="outbound_return">{t('stepper.transfer_type.option_three')}</option>
                            </select>
                        </div>
                    </div>

                    {/* OUTBOUND */}
                    {showOutboundFields && (
                        <div className="mt-4">
                            <h5>{t('stepper.outbound_transfer.title')}</h5>

                            <div className="row mt-1">
                                <div className="col-md-6 form-group">
                                    <label className="form__label">{t('stepper.dropoff_place.label')} *</label>
                                    <select
                                        className="form-control"
                                        name="dropoff_place"
                                        value={formData.dropoff_place}
                                        onChange={handleTransferChange}
                                        required
                                    >
                                        <option value="" hidden>
                                            {t('stepper.dropoff_place.placeholder')}
                                        </option>
                                        <option value="harbour">{t('contact.fields.transfer.harbour')}</option>
                                        <option value="station">{t('contact.fields.transfer.station')}</option>
                                        <option value="city">{t('contact.fields.transfer.city')}</option>
                                        <option value="other">{t('contact.fields.transfer.other')}</option>
                                    </select>
                                </div>

                                <div className="col-md-6 form-group">
                                    <label className="form__label">{t('stepper.passengers_outbound.label')} *</label>
                                    <input
                                        className="form-control"
                                        type="number"
                                        min="0"
                                        name="passengers_outbound"
                                        value={formData.passengers_outbound}
                                        onChange={handleTransferChange}
                                        required
                                    />
                                </div>
                            </div>

                            {['city', 'other'].includes(formData.dropoff_place) && (
                                <div className="row mt-1">
                                    <div className="col-md-6 form-group">
                                        <label className="form__label">
                                            {t('stepper.dropoff_place_detail.label')} *
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="dropoff_place_detail"
                                            value={formData.dropoff_place_detail}
                                            onChange={handleTransferChange}
                                            required={showOutboundFields}
                                            placeholder={t('stepper.dropoff_place_detail.placeholder')}
                                        />
                                    </div>
                                </div>
                            )}

                            {formData.dropoff_place === 'harbour' && (
                                <div className="row mt-1">
                                    <div className="col-md-6 form-group">
                                        <label className="form__label">{t('stepper.embark_detail.label')} *</label>
                                        <select
                                            className="select-form-group form-control"
                                            name="embark_detail"
                                            value={formData.embark_detail}
                                            onChange={handleTransferChange}
                                            required={showOutboundFields}
                                        >
                                            <option value="" hidden>
                                                {t('stepper.embark_detail.placeholder')}
                                            </option>
                                            <option value="Sardina - Grimaldi Lines">
                                                {t('contact.fields.embark.sardinia_grimaldi_lines')}
                                            </option>
                                            <option value="Sardinia - Sardinia Lines">
                                                {t('contact.fields.embark.sardinia_sardinia_lines')}
                                            </option>
                                            <option value="Sardinia - Moby Lines">
                                                {t('contact.fields.embark.sardinia_moby_lines')}
                                            </option>
                                            <option value="Corsica - Corsica Ferries">
                                                {t('contact.fields.embark.corsica_corsica_ferries')}
                                            </option>
                                            <option value="Corsica - Moby Lines">
                                                {t('contact.fields.embark.corsica_moby_lines')}
                                            </option>
                                            <option value="Capraia">{t('contact.fields.embark.capraia')}</option>
                                            <option value="MSC">{t('contact.fields.embark.msc')}</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* RETURN */}
                    {showReturnFields && (
                        <div className="mt-4">
                            <h5>{t('stepper.return_transfer.title')}</h5>

                            <div className="row mt-1">
                                <div className="col-md-6 form-group">
                                    <label className="form__label">{t('stepper.pickup_place.label')} *</label>
                                    <select
                                        className="form-control"
                                        name="pickup_place"
                                        value={formData.pickup_place}
                                        onChange={handleTransferChange}
                                        required
                                    >
                                        <option value="" hidden>
                                            {t('stepper.pickup_place.placeholder')}
                                        </option>
                                        <option value="harbour">{t('contact.fields.transfer.harbour')}</option>
                                        <option value="station">{t('contact.fields.transfer.station')}</option>
                                        <option value="city">{t('contact.fields.transfer.city')}</option>
                                        <option value="other">{t('contact.fields.transfer.other')}</option>
                                    </select>
                                </div>

                                <div className="col-md-6 form-group">
                                    <label className="form__label">{t('stepper.passengers_return.label')} *</label>
                                    <input
                                        className="form-control"
                                        type="number"
                                        min="0"
                                        name="passengers_return"
                                        value={formData.passengers_return}
                                        onChange={handleTransferChange}
                                        required
                                    />
                                </div>
                            </div>

                            {/* STATION */}
                            {formData.pickup_place === 'station' && (
                                <div className="row mt-1">
                                    <div className="col-xs-12 col-md-6 form-group">
                                        <DatePicker
                                            selected={timeStringToDate(formData.train_arrival_time)}
                                            onChange={handleTrainArrivalTimeChange}
                                            placeholderText={t('contact.fields.time.placeholder')}
                                            showTimeSelect
                                            showTimeSelectOnly
                                            filterTime={(time) => isPickerTimeAvailable(time, formData.checkout_date)}
                                            timeIntervals={30}
                                            timeFormat="HH:mm"
                                            dateFormat="HH:mm"
                                            className="form-control"
                                            required
                                            calendarStartDay={1}
                                        />
                                        <label className="form__label">{t('stepper.train_arrival_time.label')} *</label>
                                    </div>
                                </div>
                            )}

                            {/* CITY / OTHER */}
                            {['city', 'other'].includes(formData.pickup_place) && (
                                <div className="row mt-1">
                                    <div className="col-md-6 form-group">
                                        <label className="form__label">
                                            {t('stepper.pickup_place_detail.label')} *
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="pickup_place_detail"
                                            value={formData.pickup_place_detail}
                                            onChange={handleTransferChange}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <DatePicker
                                            selected={timeStringToDate(formData.pickup_time)}
                                            onChange={handlePickupTimeChange}
                                            placeholderText={t('contact.fields.time.placeholder')}
                                            showTimeSelect
                                            showTimeSelectOnly
                                            filterTime={(time) => isPickerTimeAvailable(time, formData.checkout_date)}
                                            timeIntervals={30}
                                            timeFormat="HH:mm"
                                            dateFormat="HH:mm"
                                            className="form-control"
                                            required
                                            calendarStartDay={1}
                                        />
                                        <label className="form__label">{t('stepper.pickup_time.label')} *</label>
                                    </div>
                                </div>
                            )}

                            {/* HARBOUR */}
                            {formData.pickup_place === 'harbour' && (
                                <div className="row mt-1">
                                    <div className="col-md-6 form-group">
                                        <label className="form__label">{t('stepper.disembark_detail.label')} *</label>
                                        <select
                                            className="form-control"
                                            name="disembark_detail"
                                            value={formData.disembark_detail}
                                            onChange={handleTransferChange}
                                            required
                                        >
                                            <option value="" hidden>
                                                {t('stepper.disembark_detail.placeholder')}
                                            </option>
                                            <option value="Sardina - Grimaldi Lines">
                                                {t('contact.fields.embark.sardinia_grimaldi_lines')}
                                            </option>
                                            <option value="Sardinia - Sardinia Lines">
                                                {t('contact.fields.embark.sardinia_sardinia_lines')}
                                            </option>
                                            <option value="Sardinia - Moby Lines">
                                                {t('contact.fields.embark.sardinia_moby_lines')}
                                            </option>
                                            <option value="Corsica - Corsica Ferries">
                                                {t('contact.fields.embark.corsica_corsica_ferries')}
                                            </option>
                                            <option value="Corsica - Moby Lines">
                                                {t('contact.fields.embark.corsica_moby_lines')}
                                            </option>
                                            <option value="Capraia">{t('contact.fields.embark.capraia')}</option>
                                            <option value="MSC">{t('contact.fields.embark.msc')}</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6 form-group">
                                        <DatePicker
                                            selected={timeStringToDate(formData.disembark_time)}
                                            onChange={handleDisembarkTimeChange}
                                            showTimeSelect
                                            showTimeSelectOnly
                                            filterTime={(time) => isPickerTimeAvailable(time, formData.checkout_date)}
                                            timeIntervals={30}
                                            timeFormat="HH:mm"
                                            dateFormat="HH:mm"
                                            placeholderText={t('contact.fields.time.placeholder')}
                                            className="form-control"
                                            required
                                            calendarStartDay={1}
                                        />
                                        <label className="form__label">{t('stepper.disembark_time.label')} *</label>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
